import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../utils/api';

const VideoCall = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [callDetails, setCallDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [callEnded, setCallEnded] = useState(false);
  const jitsiContainerRef = useRef(null);
  const jitsiApiRef = useRef(null);

  // Get username from query or fallback to participantName
  const searchParams = new URLSearchParams(location.search);
  const username = searchParams.get('username') || callDetails?.participantName || 'Anonymous';

  // Helper function to get user role
  const getUserRole = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const role = userData.role || 'Patient';
    return role;
  };

  useEffect(() => {
    console.log('VideoCall mounted');
    
    const fetchCallDetails = async () => {
      try {
        const response = await api.get(`/video-call/${appointmentId}`);
        setCallDetails(response.data.data);
        setLoading(false);
        console.log('Fetched call details:', response.data.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching call details');
        setLoading(false);
        console.error('Error fetching call details:', err);
      }
    };
    fetchCallDetails();
  }, [appointmentId]);

  // Handle call end
  const handleCallEnd = async () => {
    console.log('handleCallEnd called');
    try {
      // Immediately dispose Jitsi to prevent close page
      if (jitsiApiRef.current) {
        jitsiApiRef.current.dispose();
        jitsiApiRef.current = null;
      }
      
      // Update backend
      await api.post(`/video-call/${appointmentId}/end`);
      
      // Show our custom screen
      setCallEnded(true);
    } catch (error) {
      console.error('Error ending call:', error);
      setCallEnded(true);
    }
  };

  const handleReturnToDashboard = () => {
    const userRole = getUserRole();
    navigate(userRole === 'Doctor' ? '/dashboard/doctor' : '/dashboard/patient');
  };

  // Initialize Jitsi with External API
  useEffect(() => {
    if (!callDetails || !jitsiContainerRef.current || callEnded) return;

    let hasJoinedConference = false;

    const loadJitsi = () => {
      const script = document.createElement('script');
      script.src = 'https://meet.jit.si/external_api.js';
      script.async = true;
      script.onload = () => {
        const domain = 'meet.jit.si';
        const roomName = `WeHeal-${callDetails.roomId}`;
        
        const options = {
          roomName: roomName,
          width: '100%',
          height: '100%',
          parentNode: jitsiContainerRef.current,
          configOverwrite: {
            prejoinPageEnabled: false,
            disableDeepLinking: true,
            enableClosePage: false,
          },
          interfaceConfigOverwrite: {
            SHOW_PROMOTIONAL_CLOSE_PAGE: false,
            SHOW_JITSI_WATERMARK: false,
          },
          userInfo: {
            displayName: username,
          }
        };

        const jitsiApi = new window.JitsiMeetExternalAPI(domain, options);
        jitsiApiRef.current = jitsiApi;

        jitsiApi.addEventListener('videoConferenceJoined', () => {
          console.log('videoConferenceJoined - user is now in the call');
          hasJoinedConference = true;
        });

        // Only handle hangup if user has actually joined
        jitsiApi.addEventListener('readyToClose', () => {
          console.log('readyToClose event received, hasJoinedConference:', hasJoinedConference);
          if (hasJoinedConference) {
            console.log('User was in conference, calling handleCallEnd');
            handleCallEnd();
          } else {
            console.log('User not in conference yet, ignoring readyToClose');
          }
        });

        jitsiApi.addEventListener('videoConferenceLeft', () => {
          console.log('videoConferenceLeft event received, hasJoinedConference:', hasJoinedConference);
          if (hasJoinedConference) {
            console.log('User left conference, calling handleCallEnd');
            handleCallEnd();
          } else {
            console.log('User not in conference yet, ignoring videoConferenceLeft');
          }
        });
      };
      
      document.head.appendChild(script);
    };

    loadJitsi();

    return () => {
      if (jitsiApiRef.current) {
        try {
          jitsiApiRef.current.dispose();
        } catch (e) {
          console.error('Error disposing Jitsi:', e);
        }
        jitsiApiRef.current = null;
      }
    };
  }, [callDetails, callEnded, username, appointmentId]);

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>;
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p style={{ color: '#ef4444', marginBottom: '1rem' }}>{error}</p>
        <button
          onClick={() => navigate(-1)}
          style={{ backgroundColor: '#3b82f6', color: 'white', padding: '0.5rem 1rem', borderRadius: '0.25rem', border: 'none', cursor: 'pointer' }}
        >
          Go Back
        </button>
      </div>
    );
  }

  // Show call ended screen
  if (callEnded) {
    return (
      <div style={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f3f4f6',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 99999
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '48px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: '#10b981',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          
          <h2 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '12px'
          }}>
            Call Ended
          </h2>
          
          <p style={{
            fontSize: '16px',
            color: '#6b7280',
            marginBottom: '32px'
          }}>
            Thank you for using WeHeal video consultation
          </p>
          
          <button
            onClick={handleReturnToDashboard}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              padding: '12px 32px',
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              width: '100%',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show Jitsi container
  return (
    <div 
      ref={jitsiContainerRef}
      style={{ height: '100vh', width: '100vw', overflow: 'hidden' }}
    />
  );
};

export default VideoCall;
